import { rkLog } from './debug';

// ---------------------------------------------------------------------------
// Dynamic-import version-skew recovery (vite:preloadError).
//
// A browser tab still running a PRE-rebuild index bundle will lazily import a
// hashed chunk (e.g. SchedulePDF-XXXX.js, the disaster map/QR chunks) whose
// filename the new dist rebuild deleted — Vite empties dist/ on every build —
// so the dynamic import 404s with "Failed to fetch dynamically imported
// module" (the schedule Print button, reported 2026-06-10). This is the same
// version-skew the SW auto-update + /api/version poller (main.tsx) resolve
// EVENTUALLY (5s after load, then every 60s); but a user who clicks Print on a
// stale tab hits the missing chunk synchronously, BEFORE the poller catches up.
//
// Vite fires a cancelable `vite:preloadError` event on window when a built
// chunk fails to load. We preventDefault() (so it doesn't surface as an
// uncaught rejection) and reload to fetch the fresh bundle — exactly what the
// poller would have done, just triggered reactively on the failing import.
//
// Loop guard: if the chunk is genuinely gone even after reloading (e.g. a real
// deploy gap, not just a stale tab), reloading forever would trap the user.
// A short-lived sessionStorage stamp limits us to ONE reload per skew window;
// a second preloadError inside RELOAD_WINDOW_MS lets the error propagate so the
// normal error boundary / report path runs instead of looping.
// ---------------------------------------------------------------------------

export const PRELOAD_RELOAD_KEY = 'rk:preload-error-reload-at';
export const PRELOAD_RELOAD_WINDOW_MS = 10_000;

/**
 * Handle a single `vite:preloadError`. Returns true if a reload was triggered,
 * false if it was suppressed by the loop guard. Exported for unit testing;
 * production wires it via {@link installPreloadErrorHandler}.
 */
export function handlePreloadError(event: Event): boolean {
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY)) || 0;
  } catch {
    // sessionStorage unavailable (private mode / disabled) — fall through and
    // reload once; without persistence we can't loop-guard, but a single
    // reload is still the right first action.
  }

  const now = Date.now();
  if (now - lastReloadAt < PRELOAD_RELOAD_WINDOW_MS) {
    // We already reloaded for a preload error very recently and it happened
    // again — the chunk is genuinely missing, not just stale. Let the error
    // propagate (no preventDefault, no reload) so it isn't an infinite loop.
    rkLog('[RK] vite:preloadError again within reload window — not reloading (likely a real deploy gap)');
    return false;
  }

  // Cancelable: stop Vite from rethrowing this as an unhandled rejection.
  event.preventDefault();
  try {
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(now));
  } catch {
    // ignore — see note above
  }
  rkLog('[RK] vite:preloadError — stale chunk; reloading to fetch the current bundle');
  window.location.reload();
  return true;
}

/** Register the global vite:preloadError handler. Called once from main.tsx. */
export function installPreloadErrorHandler(): void {
  window.addEventListener('vite:preloadError', handlePreloadError);
}
